import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  packageNotificationsApi,
  type EligibleNotificationsQuery,
} from "./client";

const keys = {
  config: ["package-notifications", "config"] as const,
  autoStatus: ["package-notifications", "auto-status"] as const,
  opsStatus: ["package-notifications", "ops-status"] as const,
  eligible: (query?: EligibleNotificationsQuery) =>
    ["package-notifications", "eligible", query] as const,
  queue: (query?: Record<string, unknown>) =>
    ["package-notifications", "queue", query] as const,
  logs: (query?: Record<string, unknown>) =>
    ["package-notifications", "logs", query] as const,
  workerStatus: ["package-notifications", "worker-status"] as const,
  workerQr: ["package-notifications", "worker-qr"] as const,
};

export function usePackageNotificationConfigQuery() {
  return useQuery({
    queryKey: keys.config,
    queryFn: () => packageNotificationsApi.getConfig(),
  });
}

export function usePackageNotificationAutoStatusQuery() {
  return useQuery({
    queryKey: keys.autoStatus,
    queryFn: () => packageNotificationsApi.getAutoStatus(),
    refetchInterval: 60_000,
  });
}

export function usePackageNotificationOpsStatusQuery() {
  return useQuery({
    queryKey: keys.opsStatus,
    queryFn: () => packageNotificationsApi.getOpsStatus(),
    refetchInterval: 60_000,
  });
}

export function useUpdatePackageNotificationConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: packageNotificationsApi.updateConfig,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.config });
      qc.invalidateQueries({ queryKey: keys.autoStatus });
      qc.invalidateQueries({ queryKey: keys.opsStatus });
      qc.invalidateQueries({ queryKey: ["package-notifications", "eligible"] });
    },
  });
}

export function useEligibleNotificationsQuery(query?: EligibleNotificationsQuery) {
  return useQuery({
    queryKey: keys.eligible(query),
    queryFn: () => packageNotificationsApi.getEligible(query),
  });
}

export function useNotificationQueueQuery(query?: {
  limit?: number;
  offset?: number;
  status?: string;
}) {
  return useQuery({
    queryKey: keys.queue(query),
    queryFn: () => packageNotificationsApi.getQueue(query),
  });
}

export function useNotificationLogsQuery(query?: {
  limit?: number;
  offset?: number;
  status?: string;
}) {
  return useQuery({
    queryKey: keys.logs(query),
    queryFn: () => packageNotificationsApi.getLogs(query),
  });
}

export function useWhatsAppWorkerStatusQuery(options?: {
  refetchInterval?: number | false;
}) {
  return useQuery({
    queryKey: keys.workerStatus,
    queryFn: () => packageNotificationsApi.getWorkerStatus(),
    refetchInterval: options?.refetchInterval ?? 30_000,
  });
}

export function useWhatsAppWorkerQrQuery(enabled: boolean) {
  return useQuery({
    queryKey: keys.workerQr,
    queryFn: () => packageNotificationsApi.getWorkerQr(),
    enabled,
    refetchInterval: enabled ? 5_000 : false,
  });
}

export function useWhatsAppWorkerTestSend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: packageNotificationsApi.workerTestSend,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.workerStatus });
    },
  });
}

export function useWhatsAppWorkerLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => packageNotificationsApi.workerLogout(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.workerStatus });
      qc.invalidateQueries({ queryKey: keys.workerQr });
      qc.invalidateQueries({ queryKey: keys.opsStatus });
    },
  });
}

export function useEnqueueNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: packageNotificationsApi.enqueue,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["package-notifications", "queue"] });
      qc.invalidateQueries({ queryKey: ["package-notifications", "eligible"] });
      qc.invalidateQueries({ queryKey: keys.opsStatus });
    },
  });
}

export function useProcessNotificationQueue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input?: { batchSize?: number; queueIds?: string[] }) =>
      packageNotificationsApi.process(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["package-notifications", "queue"] });
      qc.invalidateQueries({ queryKey: ["package-notifications", "logs"] });
      qc.invalidateQueries({ queryKey: keys.workerStatus });
      qc.invalidateQueries({ queryKey: keys.opsStatus });
    },
  });
}
